import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  ZoomIn, 
  ZoomOut, 
  RotateCcw, 
  FileText, 
  ExternalLink 
} from 'lucide-react';

const MediaViewer = ({ 
  isOpen, 
  onClose, 
  resource, 
  title,
  // Gallery navigation props (optional)
  showNavigation = false,
  onPrevious,
  onNext,
  currentIndex,
  totalCount
}) => {
  // Image viewer states (from Gallery)
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Loading and error states
  const [documentLoading, setDocumentLoading] = useState(false);
  const [documentError, setDocumentError] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const imageRef = useRef(null);
  const modalRef = useRef(null);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      resetZoom();
      setDocumentLoading(false);
      setDocumentError(false);
      setImageLoading(false);
      setImageError(false);
    }
  }, [isOpen, resource]);

  const resetZoom = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  // Zoom functions (from Gallery)
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 4));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 1));
  };

  const handleZoomSlider = (e) => {
    setZoomLevel(parseFloat(e.target.value));
  };

  // Mouse wheel zoom (from Gallery)
  const handleWheel = useCallback((e) => {
    if (resource && isImageFile(resource)) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoomLevel(prev => Math.max(1, Math.min(4, prev + delta)));
    }
  }, [resource]);

  // Pan functionality (from Gallery)
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - panOffset.x,
        y: e.clientY - panOffset.y
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging && zoomLevel > 1) {
      setPanOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, zoomLevel, dragStart.x, dragStart.y]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Keyboard navigation (from Gallery)
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (isOpen) {
        switch (event.key) {
          case 'ArrowLeft':
            if (showNavigation && onPrevious) {
              event.preventDefault();
              onPrevious();
            }
            break;
          case 'ArrowRight':
            if (showNavigation && onNext) {
              event.preventDefault();
              onNext();
            }
            break;
          case 'Escape':
            event.preventDefault();
            onClose();
            break;
          case '=':
          case '+':
            event.preventDefault();
            handleZoomIn();
            break;
          case '-':
            event.preventDefault();
            handleZoomOut();
            break;
          case '0':
            event.preventDefault();
            resetZoom();
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isOpen, handleWheel, isDragging, dragStart, zoomLevel, handleMouseMove, onClose, onNext, onPrevious, showNavigation]);

  // Helper functions
  const isImageFile = (resource) => {
    if (!resource) return false;
    
    const fileName = resource.file_name || '';
    const resourceUrl = resource.resource_url || '';
    
    // Check filename first, but if it doesn't have extension, check URL
    const fileNameHasExtension = fileName.includes('.');
    const fileToCheck = fileNameHasExtension ? fileName : resourceUrl;
    
    const isImage = fileToCheck.match(/\.(jpe?g|png|gif|webp|bmp|svg)$/i);    
    return !!isImage;
  };

  const isDocumentFile = (resource) => {
    if (!resource) return false;
    
    const fileName = resource.file_name || '';
    const resourceUrl = resource.resource_url || '';
    
    // Check filename first, but if it doesn't have extension, check URL
    const fileNameHasExtension = fileName.includes('.');
    const fileToCheck = fileNameHasExtension ? fileName : resourceUrl;
    
    const isDocument = fileToCheck.match(/\.(docx?|xlsx?|pptx?|txt)$/i);    
    return !!isDocument;
  };

  const isPdfFile = (resource) => {
    if (!resource) return false;
    
    const fileName = resource.file_name || '';
    const resourceUrl = resource.resource_url || '';
    
    // Check filename first, but if it doesn't have extension, check URL
    const fileNameHasExtension = fileName.includes('.');
    const fileToCheck = fileNameHasExtension ? fileName : resourceUrl;
    
    const isPdf = fileToCheck.match(/\.pdf$/i);    
    return !!isPdf;
  };

  // Loading and error handlers
  const handleDocumentLoad = () => {
    setDocumentLoading(false);
    setDocumentError(false);
  };

  const handleDocumentError = () => {
    setDocumentLoading(false);
    setDocumentError(true);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  if (!isOpen || !resource) return null;

  return (
    <>
      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #ef4444;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        .slider::-moz-range-thumb {
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #ef4444;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      `}</style>
      
      <div className="fixed inset-0 bg-black bg-opacity-75 flex flex-col items-center justify-center z-50 p-4">
        {isImageFile(resource) ? (
          // Image Viewer (Gallery-style)
          <>
            <div className="relative w-full h-full max-w-[90vw] max-h-[80vh] mb-4">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
              >
                <X className="w-8 h-8" />
              </button>

              {/* Navigation arrows - only show if navigation is enabled */}
              {showNavigation && onPrevious && (
                <button
                  onClick={onPrevious}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-2 transition-all hover:bg-opacity-70"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
              )}

              {showNavigation && onNext && (
                <button
                  onClick={onNext}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-2 transition-all hover:bg-opacity-70"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              )}

              {/* Image counter overlay - only show if navigation is enabled */}
              {showNavigation && currentIndex !== undefined && totalCount > 1 && (
                <div className="absolute top-4 left-4 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded-full">
                  {currentIndex + 1} / {totalCount}
                </div>
              )}

              {/* Main image container */}
              <div 
                ref={modalRef}
                className="overflow-hidden w-full h-full flex items-center justify-center"
                style={{ cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
              >
                {imageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2"></div>
                      <p className="text-sm text-white">Loading image...</p>
                    </div>
                  </div>
                )}
                
                {imageError ? (
                  <div className="flex items-center justify-center w-full h-full">
                    <div className="text-center text-white">
                      <FileText className="w-12 h-12 text-white mx-auto mb-2" />
                      <p className="mb-2">Failed to load image</p>
                      <a
                        href={resource.resource_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-400 hover:text-red-300 text-sm underline"
                      >
                        Open in new tab
                      </a>
                    </div>
                  </div>
                ) : (
                  <img
                    ref={imageRef}
                    src={resource.resource_url}
                    alt={title}
                    className="transition-transform duration-200 select-none"
                    style={{
                      maxWidth: zoomLevel === 1 ? '100%' : `${Math.min(500 * zoomLevel, window.innerWidth * 0.9)}px`,
                      maxHeight: zoomLevel === 1 ? '100%' : `${Math.min(500 * zoomLevel, window.innerHeight * 0.8)}px`,
                      objectFit: 'contain',
                      transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
                      display: imageLoading ? 'none' : 'block'
                    }}
                    onMouseDown={handleMouseDown}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    onLoadStart={() => setImageLoading(true)}
                    draggable={false}
                  />
                )}
              </div>
            </div>

            {/* Zoom controls */}
            <div className="bg-black bg-opacity-70 rounded-full px-4 py-2 flex items-center space-x-3">
              <button
                onClick={handleZoomOut}
                className="text-white hover:text-gray-300 p-1"
                disabled={zoomLevel <= 1}
                aria-label="Zoom out"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              
              <div className="flex items-center space-x-2">
                <span className="text-white text-sm min-w-[3rem] text-center">
                  {Math.round(zoomLevel * 100)}%
                </span>
                <input
                  type="range"
                  min="1"
                  max="4"
                  step="0.1"
                  value={zoomLevel}
                  onChange={handleZoomSlider}
                  className="w-24 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${((zoomLevel - 1) / 3) * 100}%, #374151 ${((zoomLevel - 1) / 3) * 100}%, #374151 100%)`
                  }}
                />
              </div>
              
              <button
                onClick={handleZoomIn}
                className="text-white hover:text-gray-300 p-1"
                disabled={zoomLevel >= 4}
                aria-label="Zoom in"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
              
              <button
                onClick={resetZoom}
                className="text-white hover:text-gray-300 p-1 ml-2"
                aria-label="Reset zoom"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>

            {/* Keyboard hint */}
            <div className="text-gray-400 text-xs mt-3 text-center space-y-1">
              {showNavigation && totalCount > 1 && (
                <div>Use ← → arrow keys or click arrows to navigate</div>
              )}
              <div>Scroll to zoom • +/- keys to zoom • 0 to reset • Drag to pan when zoomed</div>
            </div>
          </>
        ) : (
          // Document Viewer
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-hidden relative">
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b pr-16">
              <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
            </div>
            
            <div className="p-4">
              {isPdfFile(resource) ? (
                // PDF Viewer - Direct embedding
                <div className="h-96 relative">
                  {documentLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg border z-10">
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-2"></div>
                        <p className="text-sm text-gray-600">Loading PDF...</p>
                      </div>
                    </div>
                  )}
                  {documentError ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg border">
                      <div className="text-center">
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600 mb-2">Failed to load PDF</p>
                        <div className="space-y-2">
                          <button
                            onClick={() => {
                              setDocumentError(false);
                              setDocumentLoading(true);
                              const iframe = document.querySelector('#pdf-iframe');
                              if (iframe) {
                                const currentSrc = iframe.src;
                                iframe.src = '';
                                iframe.src = currentSrc;
                              }
                            }}
                            className="text-red-600 hover:text-red-700 text-sm underline block"
                          >
                            Try again
                          </button>
                          <a
                            href={resource.resource_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-sm underline block"
                          >
                            Open in new tab
                          </a>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <iframe
                      id="pdf-iframe"
                      src={`https://docs.google.com/gview?url=${encodeURIComponent(resource.resource_url)}&embedded=true`}
                      width="100%"
                      height="100%"
                      className="border rounded-lg"
                      title={title}
                      onLoad={handleDocumentLoad}
                      onError={handleDocumentError}
                      onLoadStart={() => setDocumentLoading(true)}
                      style={{ display: documentError ? 'none' : 'block' }}
                    />
                  )}
                </div>
              ) : isDocumentFile(resource) ? (
                // Other Document Viewer - Google Docs
                <div className="h-96 relative">
                  {documentLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg border z-10">
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-2"></div>
                        <p className="text-sm text-gray-600">Loading document...</p>
                      </div>
                    </div>
                  )}
                  {documentError ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg border">
                      <div className="text-center">
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600 mb-2">Failed to load document</p>
                        <div className="space-y-2">
                          <button
                            onClick={() => {
                              setDocumentError(false);
                              setDocumentLoading(true);
                              const iframe = document.querySelector('#document-iframe');
                              if (iframe) {
                                const currentSrc = iframe.src;
                                iframe.src = '';
                                iframe.src = currentSrc;
                              }
                            }}
                            className="text-red-600 hover:text-red-700 text-sm underline block"
                          >
                            Try again
                          </button>
                          <a
                            href={resource.resource_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-sm underline block"
                          >
                            Open in new tab
                          </a>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <iframe
                      id="document-iframe"
                      src={`https://docs.google.com/gview?url=${encodeURIComponent(resource.resource_url)}&embedded=true`}
                      width="100%"
                      height="100%"
                      className="border rounded-lg"
                      title={title}
                      onLoad={handleDocumentLoad}
                      onError={handleDocumentError}
                      onLoadStart={() => setDocumentLoading(true)}
                      style={{ display: documentError ? 'none' : 'block' }}
                    />
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">Viewer not available for this file type</p>
                  <a
                    href={resource.resource_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Document
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MediaViewer;